import React, { useCallback, useMemo } from "react";

import { useLocalStorage } from "react-use";
import useTranslations from "../../hooks/useTranslations";

import { GridIcon, ListIcon } from "../icons";
import { Button, ButtonGroup, TailSpin } from "../wombat-ui";

interface ListProps<T> {
  data: T[];
  renderAsList?: (data: T, index: number) => React.ReactNode;
  renderAsCard?: (data: T, index: number) => React.ReactNode;
  renderEmptyState?: () => React.ReactNode;
  listOnly?: boolean;
  cardOnly?: boolean;
  cardPerRow?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  defaultDisplay?: "card" | "list";
  loading?: boolean;
  children?: React.ReactNode;
  id: string;
}

function List<T>({
  data,
  renderAsList,
  renderAsCard,
  renderEmptyState,
  listOnly = false,
  cardOnly = false,
  cardPerRow = 4,
  loading = false,
  children,
  id,
}: ListProps<T>) {
  const t = useTranslations();
  const [display, setDisplay] = useLocalStorage(
    `list-display-${id}`,
    cardOnly ? "card" : "list"
  );

  const setAsCard = useCallback(() => setDisplay("card"), []);
  const setAsList = useCallback(() => setDisplay("list"), []);

  return useMemo(() => {
    return (
      <div className="p-3 bg-white rounded">
        {/* Display type */}
        {children && (
          <div className="flex justify-between pb-6 space-x-2">
            {children}
            {!listOnly && !cardOnly && (
              <ButtonGroup scale="sm">
                <Button
                  icon={GridIcon}
                  onClick={setAsCard}
                  variant={display === "card" ? "primary" : undefined}
                />
                <Button
                  icon={ListIcon}
                  onClick={setAsList}
                  variant={display === "list" ? "primary" : undefined}
                />
              </ButtonGroup>
            )}
          </div>
        )}
        {loading && (
          <div className="flex items-center justify-center min-h-xl">
            <TailSpin className="w-12 h-12" id="loader" />
          </div>
        )}
        {!loading && data.length === 0 && renderEmptyState && (
          <div
            className={`flex flex-col min-h-xs sm:min-h-sm items-center justify-center`}
          >
            {renderEmptyState()}
          </div>
        )}
        {!loading && display === "card" && renderAsCard && data.length > 0 && (
          <div className={`grid grid-cols-${cardPerRow} gap-4`} id={id}>
            {data.map((d: T, index) => renderAsCard(d, index))}
          </div>
        )}
        {!loading && display === "list" && renderAsList && data.length > 0 && (
          <ul className={`divide-y divide-gray-200`} id={id}>
            {data.map((d: T, index) => renderAsList(d, index))}
          </ul>
        )}
      </div>
    );
  }, [data, renderAsList, renderAsCard, cardPerRow, display, children]);
}

export default List;
